/**
 * Module pour harmoniser les modales de services selon le service sélectionné
 * Migration du script public/js/modal-service-harmonization.js vers TypeScript
 */

type ServiceType = 
  | 'web-service'
  | 'strategie-service'
  | 'facturation-service'
  | 'rgpd-service'
  | 'cybersecurite-service'
  | 'donnees-service'
  | 'processus-service';

interface ServiceMapping {
  keywords: string[];
  className: ServiceType;
}

class ModalServiceHarmonizer {
  private observer: MutationObserver | null = null;
  private modal: HTMLElement | null = null;

  private readonly serviceMapping: ServiceMapping[] = [
    {
      keywords: ['Développement Web'],
      className: 'web-service'
    },
    {
      keywords: ['Stratégie Numérique'],
      className: 'strategie-service'
    },
    {
      keywords: ['Facture Électronique'],
      className: 'facturation-service'
    },
    {
      keywords: ['RGPD'],
      className: 'rgpd-service'
    },
    {
      keywords: ['Cybersécurité', 'Cybersecurite'],
      className: 'cybersecurite-service'
    },
    {
      keywords: ['Analyse de Données', 'Donnees'],
      className: 'donnees-service'
    },
    {
      keywords: ['Digitalisation', 'Processus'],
      className: 'processus-service'
    }
  ];

  private readonly allServiceClasses: ServiceType[] = [
    'web-service',
    'strategie-service', 
    'facturation-service',
    'rgpd-service',
    'cybersecurite-service',
    'donnees-service',
    'processus-service'
  ];

  constructor() {
    this.initialize();
  }

  private initialize(): void {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupObserver();
    });
  }

  private setupObserver(): void {
    this.modal = document.getElementById('service-detail-modal');
    if (!this.modal) return;

    // Observer qui détecte l'ouverture des modales de services
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'class' &&
            this.modal?.classList.contains('active')) {
          
          // Délai pour s'assurer que le contenu est chargé
          setTimeout(() => {
            this.harmonizeServiceModal();
          }, 100);
        }
      });
    });

    this.observer.observe(this.modal, { attributes: true });
  }

  private harmonizeServiceModal(): void {
    const modalContent = document.querySelector('.service-modal-content');
    if (!modalContent || !this.modal) return;

    // Retirer toutes les classes de service précédentes
    this.removeAllServiceClasses();

    // Déterminer et appliquer la classe du service actuel
    const serviceClass = this.detectServiceType(modalContent);
    if (serviceClass) {
      this.modal.classList.add(serviceClass);
    }
  }

  private removeAllServiceClasses(): void {
    if (!this.modal) return;
    
    this.allServiceClasses.forEach(className => {
      this.modal!.classList.remove(className);
    });
  }

  private detectServiceType(modalContent: Element): ServiceType | null {
    const titleElement = modalContent.querySelector('h3');
    if (!titleElement?.textContent) return null;

    const serviceTitle = titleElement.textContent.trim();

    // Rechercher le service correspondant
    for (const service of this.serviceMapping) {
      if (service.keywords.some(keyword => serviceTitle.includes(keyword))) {
        return service.className;
      }
    }

    return null;
  }

  /**
   * Méthode publique pour forcer l'harmonisation
   * Utile pour les cas où la modale est déjà ouverte
   */
  public forceHarmonize(): void {
    this.harmonizeServiceModal();
  }

  /**
   * Méthode pour ajouter un nouveau mapping de service
   */
  public addServiceMapping(keywords: string[], className: ServiceType): void {
    this.serviceMapping.push({ keywords, className });
    
    if (!this.allServiceClasses.includes(className)) {
      this.allServiceClasses.push(className);
    }
  }

  /**
   * Nettoyage des observers
   */
  public destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// Auto-instantiation
export const modalServiceHarmonizer = new ModalServiceHarmonizer();
export default ModalServiceHarmonizer;